import React from "react";
import { IoMdEye, IoMdEyeOff } from "react-icons/io";

type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> & {
  label?: string;
  error?: string;
  containerClassName?: string;
  labelClassName?: string;
  size?: "sm" | "md" | "lg";
};

const sizeClasses: Record<NonNullable<InputProps["size"]>, string> = {
  sm: "h-9 px-3 text-sm",
  md: "h-10 px-4 text-md",
  lg: "h-[50px] px-4 text-lg",
};

export default function Input({
  label,
  error,
  id,
  containerClassName = "",
  labelClassName = "",
  size = "md",
  className = "",
  type,
  ...props
}: InputProps) {
  const generatedId = React.useId();
  const inputId = id || generatedId;
  const isPasswordField = type === "password";
  const [showPassword, setShowPassword] = React.useState(false);
  const inputType = isPasswordField ? (showPassword ? "text" : "password") : type;

  return (
    <div className={containerClassName}>
      {label ? (
        <label
          htmlFor={inputId}
          className={["mb-2 block text-sm font-medium text-black", labelClassName]
            .filter(Boolean)
            .join(" ")}
        >
          {label}
        </label>
      ) : null}

      <div className="relative">
        <input
          id={inputId}
          type={inputType}
          className={[
            "w-full rounded-[8px] border border-[#E1E1E1] bg-white text-black placeholder:text-black/55",
            sizeClasses[size],
            isPasswordField ? "pr-10" : "",
            "outline-none transition-colors duration-200 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue",
            error ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "",
            className,
          ]
            .filter(Boolean)
            .join(" ")}
          {...props}
        />

        {isPasswordField ? (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setShowPassword((prev) => !prev)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-black/60 transition-colors hover:text-black"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <IoMdEyeOff size={18} /> : <IoMdEye size={18} />}
          </button>
        ) : null}
      </div>

      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
    </div>
  );
}

